import { Box, Fade, Paper } from '@material-ui/core'
import { createContext, useCallback, useMemo, useState } from 'react'
import { useEffect } from 'react'

import { Canvas as CanvasType } from 'fabric/fabric-impl'
import Canvas from '../Canvas/Canvas'


import styled from 'styled-components'
import PhysicsWorld from '../../logic/loop/PhysicsWorld'
import { shape, theme } from '../../theme'
import NodeInventory from '../NodeInventory/NodeInventory'
import ParticleCount from '../ParticleCount/ParticleCount'
import SelectedNode from '../SelectedNode/SelectedNode'
import ViewToolbar from '../ViewToolbar/ViewToolbar'
import ManaWorld from '../../logic/loop/ManaWorld'
import NodeRegistry from '../../logic/mana/nodes/NodeRegistry'
import editModes from '../../input/editModes'
import SimpleEntity from '../../logic/physics/SimpleEntity'
import SelectedEntity from '../SelectedEntity/SelectedEntity'

import CastingEntity from '../../logic/physics/CastingEntity'
import GameGlobals, { GameMode } from './GameGlobals'
import ManaNode from '../../logic/mana/nodes/ManaNode'
import StyledIconButton from '../lookAndFeel/StyledIconButton'

import FullscreenIcon from 'mdi-material-ui/Fullscreen'
import FullscreenExitIcon from 'mdi-material-ui/FullscreenExit'
import EntityCount from '../EntityCount/EntityCount'
import { Tooltip } from '@material-ui/core'
import ViewportLock from '../ViewportLock/ViewportLock'
import ImportExport from '../ImportExport/ImportExport'
import PIXICanvas from '../PIXICanvas/PIXICanvas'
import { Application } from 'pixi.js'

const PanelsWrapper = styled.div`
	flex: 1;
	display: grid;
	gap: 8px 4rem;
	grid-auto-flow: column;
	align-items: stretch;
	grid-auto-columns: 1fr;
	grid-template-rows: min-content auto min-content;

	padding: 8px;
	background: ${theme.palette.primary.main};
	border-radius: ${shape.borderRadius};
	color: ${theme.palette.getContrastText(theme.palette.primary.main)};
`

const StyledPaper = styled(Paper)`

	position: relative;
	overflow: hidden;

	&:focus {
		outline: none;
	}
`

const ToolbarTitle = styled.div`
	display: flex;
	align-items: center;

	padding: 6px 12px;

	white-space: nowrap;
`

export const GamePageContext = createContext({
	manaWorld: undefined as (undefined | ManaWorld),
	physicsWorld: undefined as (undefined | PhysicsWorld),
	gameContainer: undefined as any
})

export default function GamePage({
	allowImportExport = false,
	side = undefined as any,
	onLoaded = undefined as any,
	sandboxMode = false,
}) {

	GameGlobals.gameMode = sandboxMode ? GameMode.SANDBOX : GameMode.NORMAL

	const [isFullscreen, setIsFullscreen] = useState(false)

	const [nodes] = useState(NodeRegistry.getNodes())
	const [selectedNode, setSelectedNode] = useState(undefined as any)

	const [manaCanvas, setManaCanvas] = useState<CanvasType>()
	const [, setPhysicsCanvas] = useState<Application>()

	const [physicsWorld, setPhysicsWorld] = useState<PhysicsWorld>()
	const [manaWorld, setManaWorld] = useState<ManaWorld>()

	const [selectedEntity, setSelectedEntity] = useState<SimpleEntity>()

	const [selectedTooltip, setSelectedTooltip] = useState<any>()

	const [gameContainer, setGameContainer] = useState<any>()

	const handleCanvasCreated = useCallback((canvas: CanvasType) => {
		setManaCanvas(canvas)

		const handleObjectSelected = (event: any) => {
			const target = event.selected[0]
			if (target && target.type === 'linkedgroup' && target.linkedObject instanceof ManaNode) {
				const obj = target.linkedObject as ManaNode

				setSelectedTooltip(obj.getTooltip())
			}
		}

		const handleObjectUnselected = (event: any) => {
			setSelectedTooltip(undefined)
		}

		canvas.on('selection:created', handleObjectSelected)
		canvas.on('selection:cleared', handleObjectUnselected)

		return () => {
			canvas.off('selection:created', handleObjectSelected)
			canvas.off('selection:cleared', handleObjectUnselected)
		}
	}, [])

	useEffect(() => {
		if (manaWorld) {
			manaWorld.bindCanvas(manaCanvas)

			return () => {
				manaWorld.bindCanvas(undefined)
			}
		}
	}, [manaCanvas, manaWorld])

	// onMount
	useEffect(() => {
		GameGlobals.setSelectedEntity = (ent: any) => {
			setSelectedEntity(ent)
			GameGlobals.selectedEntity = ent
			if (ent instanceof CastingEntity) {
				setManaWorld(ent.manaWorld)
			} else {
				setManaWorld(undefined)
			}
		}
	}, [])

	const handlePhysicsCanvasCreated = useCallback((pixi: Application) => {
		setPhysicsCanvas(pixi)

		const physicsWorld = new PhysicsWorld()
		physicsWorld.bindCanvas(pixi)

		setPhysicsWorld(physicsWorld)
	}, [])

	useEffect(() => {
		editModes.selectedNode = selectedNode
	}, [selectedNode])

	useEffect(() => {
		if (physicsWorld && manaCanvas) {
			onLoaded?.(physicsWorld)
		}
	}, [physicsWorld, manaCanvas, onLoaded])

	// handle dispose
	useEffect(() => {
		if (physicsWorld) {

			return () => {
				physicsWorld.dispose()
			}
		}
	}, [physicsWorld])

	const contextMemo = useMemo(() => ({ manaWorld, physicsWorld, gameContainer }), [manaWorld, physicsWorld, gameContainer])

	const manaWorldConnectedStyle = { display: Boolean(manaWorld) ? undefined : 'none' }

	return (
		<GamePageContext.Provider value={contextMemo}>
			{side}
			<PanelsWrapper ref={node => setGameContainer(node)}>
				<ViewToolbar style={manaWorldConnectedStyle}>
					{allowImportExport && (
						<ImportExport manaWorld={manaWorld} />
					)}
					<ToolbarTitle>Mana Graph</ToolbarTitle>
					<Box flex={1} />
					<NodeInventory
						nodes={nodes}
						value={selectedNode}
						onChange={setSelectedNode}
					/>
				</ViewToolbar>
				<Fade in={Boolean(manaWorld)} timeout={1000}>
					<StyledPaper elevation={0} tabIndex={0} style={manaWorldConnectedStyle}>
						<Canvas canvasType="mana" onCanvasCreated={handleCanvasCreated} />
						{selectedTooltip && (
							<Box position="absolute" bottom="0" left="0" margin="8px">
								<Paper elevation={0} style={{ border: `2px solid ${theme.palette.primary.main}` }}>
									<Box p="8px" display="flex" flexDirection="column" gridGap="8px">
										{selectedTooltip}
									</Box>
								</Paper>
							</Box>
						)}
					</StyledPaper>
				</Fade>
				<ViewToolbar style={manaWorldConnectedStyle}>
					<ParticleCount manaWorld={manaWorld} />
					<Box flex={1} />
					<SelectedNode selectedNode={selectedNode} />
				</ViewToolbar>




				<ViewToolbar>
					<ToolbarTitle>Physical World</ToolbarTitle>
					<Box flex={1} />
					<SelectedEntity entity={selectedEntity} />
					<Tooltip title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}>
						<StyledIconButton
							color="inherit"
							onClick={() => {
								if (isFullscreen) {
									document.exitFullscreen()
									setIsFullscreen(false)
								} else {
									gameContainer.requestFullscreen();
									setIsFullscreen(true)
								}
							}}
						>
							{isFullscreen ? (
								<FullscreenExitIcon />
							) : (
								<FullscreenIcon />
							)}
						</StyledIconButton>
					</Tooltip>
				</ViewToolbar>
				<Fade in={Boolean(physicsWorld)} timeout={1000}>
					<StyledPaper elevation={0} tabIndex={0}>
						<PIXICanvas onAppCreated={handlePhysicsCanvasCreated} />
					</StyledPaper>
				</Fade>
				<ViewToolbar>
					<EntityCount physicsWorld={physicsWorld} />
					<Box flex={1} />

					<ViewportLock physicsWorld={physicsWorld} />
				</ViewToolbar>


			</PanelsWrapper>
		</GamePageContext.Provider>
	)
}
